import { AxiosResponse } from "axios";
import businessPortalClient from "../businessPortalClient";
import { VehicleAssignmentsMyFleetOverviewWithPagination } from "../models/delegations/DelegatedParkingProductsModel";
import { getQueryString } from "../utils/url.utils";

const baseUrl = "/api/parkingproducts";

const getParkingProductsUrl = (
  seasonTicketOwnerCrmId: string,
  location: string
) => `${baseUrl}/getparkingproducts/${seasonTicketOwnerCrmId}/${location}`;

const getDelegateParkingRightsUrl = () => `${baseUrl}/delegateparkingrights`;

const getUpdateDelegationUrl = () => `${baseUrl}/updatedelegation`;

const getDelegatedParkingRightsUrl = (
  seasonTicketOwnerCrmId: string,
  language: string
) =>
  `${baseUrl}/getdelegatedparkingrights/${seasonTicketOwnerCrmId}/${language}`;

const getLocatedParkingProductsUrl = (
  seasonTicketOwnerCrmId: string,
  language: string
) =>
  `${baseUrl}/getlocatedparkingproducts/${seasonTicketOwnerCrmId}/${language}`;

const getDelegeeDetailsUrl = (registrationId: string, language: string, seasonTicketOwnerCrmId: string) =>
  `${baseUrl}/getdelegeedetails/${registrationId}/${language}/${seasonTicketOwnerCrmId}`;

const getDeleteDelegeeUrl = (registrationId: string, seasonTicketOwnerCrmId: string) =>
  `${baseUrl}/deletedelegee/${registrationId}/${seasonTicketOwnerCrmId}`;

const getResendDelegeeInviteUrl = (
  registrationId: string,
  seasonTicketOwnerCrmId: string
) =>
  `${baseUrl}/ResendDelegeeInvitation/${registrationId}/${seasonTicketOwnerCrmId}`;

const getDelegeesUrl = (seasonTicketOwnerCrmId: string, language: string) =>
  `${baseUrl}/delegees/${seasonTicketOwnerCrmId}/${language}`;

const getRevokeParkingRightsUrl = () => `${baseUrl}/revokeparkingrights`;

const getDeleteDelegeesUrl = () => `${baseUrl}/deletedelegations`;

const getValidateDelegeesUrl = (seasonTicketOwnerCrmId: string) =>
    `${baseUrl}/ValidateDelegees/${seasonTicketOwnerCrmId}`;

const getDelegeesValidationResultUrl = (seasonTicketOwnerCrmId: string, fileReference: string, language: string) =>
    `${baseUrl}/BulkInvitations/${seasonTicketOwnerCrmId}/file-reference/${fileReference}/validationresults/${language}`;

const getDelegeesInvitationResultUrl = (seasonTicketOwnerCrmId: string, fileReference: string, language: string) =>
    `${baseUrl}/BulkInvitations/${seasonTicketOwnerCrmId}/file-reference/${fileReference}/invitationresults/${language}`;

const getStartBulkInvitationsUrl = (seasonTicketOwnerCrmId: string, fileReference: string) =>
    `${baseUrl}/BulkInvitations/${seasonTicketOwnerCrmId}/file-reference/${fileReference}`;

const getBulkImportProgressUrl = (seasonTicketOwnerCrmId: string, fileReference: string) =>
    `${baseUrl}/BulkInvitations/${seasonTicketOwnerCrmId}/file-reference/${fileReference}/progress`;

const getBulkImportStatusUrl = (seasonTicketOwnerCrmId: string) =>
    `${baseUrl}/BulkInvitations/${seasonTicketOwnerCrmId}/status`;

const getAssignVehicleUrl = (seasonTicketOwnerCrmId: string) =>
  `${baseUrl}/VehicleAssignments/${seasonTicketOwnerCrmId}`;

const getUpdateVehicleAssignmentUrl = (seasonTicketOwnerCrmId: string, aggregateId: string) =>
  `${baseUrl}/VehicleAssignments/${seasonTicketOwnerCrmId}/${aggregateId}`;

const getVehicleAssignmentUrl = (aggregateId: string) =>
  `${baseUrl}/VehicleAssignments/${aggregateId}`;

const getRevokeVehicleAssignmentsUrl = () => `${baseUrl}/VehicleAssignments/revoke`;

const getDeleteVehicleAssignmentsUrl = () => `${baseUrl}/VehicleAssignments/delete`;

const getStartBulkVehicleAssignmentUrl = (seasonTicketOwnerCrmId: string, fileReference: string) =>
  `${baseUrl}/VehicleAssignments/${seasonTicketOwnerCrmId}/file-reference/${fileReference}`;

const getBulkVehicleAssignmentsStatusUrl = (seasonTicketOwnerCrmId: string) =>
    `${baseUrl}/BulkVehicleAssignments/${seasonTicketOwnerCrmId}/status`;

const getValidateVehicleBulkUploadUrl = () => `${baseUrl}/VehicleAssignments/validate-bulk-upload`;

const getBulkVehicleAssignmentsErrorResultUrl = (seasonTicketOwnerCrmId: string, fileReference: string, language: string) => {
  const qs = getQueryString({ language });
  return `${baseUrl}/VehicleAssignments/${seasonTicketOwnerCrmId}/file-reference/${fileReference}/assignmenterrors?${qs}`;
};

export type GetVehicleAssignmentsQuery = {
  seasonTicketOwnerCrmId: string;
  placeId: number;
  language: string;
  pmcIds?: number[];
  numberPlateValue?: string;
  page: number;
  pageSize: number;
  assignedDateStart?: string;
  assignedDateEnd?: string;
  startedDateStart?: string;
  startedDateEnd?: string;
  activationDateStart?: string;
  activationDateEnd?: string;
  expirationDateStart?: string;
  expirationDateEnd?: string;
  activeSessionSinceStart?: string;
  activeSessionSinceEnd?: string;
  sortBy?: 'description' | 'numberPlate' | 'assignedDate' | 'StartDate';
  ascending?: boolean;
  description?: string;
  StartedDateStart?: string;
  StartedDateEnd?: string;
};

export type GetVehicleAssignmentsMyFleetQuery = Omit<GetVehicleAssignmentsQuery, 'placeId'>;

function getVehicleAssignmentsUrl(req: GetVehicleAssignmentsQuery) {
  const { seasonTicketOwnerCrmId, placeId, ...others } = req;
  const qs = getQueryString(others);

  return `${baseUrl}/VehicleAssignments/${seasonTicketOwnerCrmId}/${placeId}?${qs}`
}

function getVehicleAssignmentsMyFleetUrl(req: GetVehicleAssignmentsMyFleetQuery) {
  const { seasonTicketOwnerCrmId, ...others } = req;
  const qs = getQueryString(others);
  return `${baseUrl}/VehicleAssignments/fleet/${seasonTicketOwnerCrmId}?${qs}`;
}

const getCalculateWhitelistDeltaUrl = () =>
  `${baseUrl}/VehicleAssignments/calculate-whitelist-delta`;

const getBulkVehicleImportProgressUrl = (seasonTicketOwnerCrmId: string, fileReference: string) =>
  `${baseUrl}/VehicleAssignments/${seasonTicketOwnerCrmId}/file-reference/${fileReference}/progress`;

async function getVehicleAssignmentsMyFleetOverview(req: GetVehicleAssignmentsMyFleetQuery) 
  :  Promise<AxiosResponse<VehicleAssignmentsMyFleetOverviewWithPagination>> {

  type vehicleAssignmentProductDto = {
    parkingRightId: string;
    pmcId: number;
    pmcName: string;
    locationName: string;
    activationDate?: string;
    expirationDate?: string;
    activeSessionSince?: string | null;
  }
  type vehicleAssignmentsDto = {
    items: Array<{
      vehicleAssignmentId: string;
      description?: string;
      numberPlate: {
        countryCode: string;
        value: string;
      };
      createdAt: string;
      product: vehicleAssignmentProductDto | null
    }>;
    filteredCount: number;
    totalUniqueVehicles: number;
  };

  const map = (dto: vehicleAssignmentsDto): VehicleAssignmentsMyFleetOverviewWithPagination => {
    const pages = Math.ceil(dto.filteredCount / req.pageSize);

    return {
      vehicleAssignments: dto.items.map((x) => ({
        vehicleAssignmentId: x.vehicleAssignmentId,
        createdAt: new Date(x.createdAt),
        vehicle: {
          countryCode: x.numberPlate.countryCode,
          identifier: x.numberPlate.value,
          description: x.description,
        },
        product: x.product
          ? {
              parkingRightId: x.product.parkingRightId,
              pmcId: x.product.pmcId,
              pmcName: x.product.pmcName,
              locationName: x.product.locationName,
              activationDate: x.product.activationDate ? new Date(x.product.activationDate) : undefined,
              expirationDate: x.product.expirationDate ? new Date(x.product.expirationDate) : undefined,
              activeSessionSince: x.product.activeSessionSince ?? null,
            }
          : null
      })),
      pages,
      totalRecords: dto.filteredCount,
      totalUniqueVehicles: dto.totalUniqueVehicles,
    };
  };

  const { seasonTicketOwnerCrmId, ...others } = req;
  const qs = getQueryString(others);
  const url = `${baseUrl}/VehicleAssignments/myfleet/${seasonTicketOwnerCrmId}?${qs}`;
  const response = await businessPortalClient.get<vehicleAssignmentsDto>(url);

  return {
    ...response,
    data: response.data && map(response.data),
  };
}

export default {
  getParkingProductsUrl,
  getDelegateParkingRightsUrl,
  getUpdateDelegationUrl,
  getDelegatedParkingRightsUrl,
  getLocatedParkingProductsUrl,
  getDelegeeDetailsUrl,
  getDeleteDelegeeUrl,
  getResendDelegeeInviteUrl,
  getDelegeesUrl,
  getRevokeParkingRightsUrl,
  getDeleteDelegeesUrl,
  getValidateDelegeesUrl,
  getDelegeesValidationResultUrl,
  getDelegeesInvitationResultUrl,
  getStartBulkInvitationsUrl,
  getBulkImportProgressUrl,
  getBulkImportStatusUrl,
  getAssignVehicleUrl,
  getVehicleAssignmentsUrl,
  getUpdateVehicleAssignmentUrl,
  getVehicleAssignmentUrl,
  getVehicleAssignmentsMyFleetUrl,
  getRevokeVehicleAssignmentsUrl,
  getDeleteVehicleAssignmentsUrl,
  getCalculateWhitelistDeltaUrl,
  getStartBulkVehicleAssignmentUrl,
  getBulkVehicleImportProgressUrl,
  getBulkVehicleAssignmentsStatusUrl,
  getValidateVehicleBulkUploadUrl,
  getBulkVehicleAssignmentsErrorResultUrl,
  getVehicleAssignmentsMyFleetOverview
};
